/**
 * Onboarding Wizard Constants & Configuration
 */

import type { OnboardingStepConfig } from './types';

export const DAYS_OF_WEEK = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

/**
 * TIME_OPTIONS: Pre-calculated time slots in 30-minute intervals.
 */
export const TIME_OPTIONS = Array.from({ length: 24 * 2 }, (_, i) => {
  const hour = Math.floor(i / 2);
  const minute = i % 2 === 0 ? '00' : '30';
  const hourStr = hour.toString().padStart(2, '0');
  return { value: `${hourStr}:${minute}`, label: formatTime(`${hourStr}:${minute}`) };
});

export function formatTime(time: string): string {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${hour12}:${minutes} ${ampm}`;
}

/**
 * Generate a URL-friendly slug from a business name.
 */
export function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Get the browser's detected timezone via Intl API.
 * Fallback to 'America/New_York' if detection fails.
 */
export function getDetectedTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'America/New_York';
  }
}

/**
 * Get all supported IANA timezones.
 * This list is populated via Intl.supportedValuesOf if available, otherwise a curated fallback.
 */
export function getAvailableTimezones(): string[] {
  try {
    // Modern browsers support this (Chrome 106+, Firefox 121+, Safari 17.4+, Edge 106+)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (Intl as any).supportedValuesOf?.('timeZone') ?? getTimezoneFallback();
  } catch {
    return getTimezoneFallback();
  }
}

/**
 * Fallback timezone list for browsers that don't support Intl.supportedValuesOf.
 * This is a curated list of common zones grouped by region.
 */
function getTimezoneFallback(): string[] {
  return [
    // Americas
    'America/Anchorage',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Mexico_City',
    'America/New_York',
    'America/Phoenix',
    'America/Toronto',
    'America/Vancouver',
    'America/Argentina/Buenos_Aires',
    'America/Sao_Paulo',

    // Europe
    'Europe/Amsterdam',
    'Europe/Berlin',
    'Europe/Brussels',
    'Europe/Dublin',
    'Europe/Istanbul',
    'Europe/London',
    'Europe/Madrid',
    'Europe/Paris',
    'Europe/Rome',
    'Europe/Vienna',
    'Europe/Zurich',
    'Europe/Moscow',

    // Asia
    'Asia/Bangkok',
    'Asia/Beijing',
    'Asia/Dubai',
    'Asia/Hong_Kong',
    'Asia/Jakarta',
    'Asia/Kolkata',
    'Asia/Seoul',
    'Asia/Shanghai',
    'Asia/Singapore',
    'Asia/Tokyo',

    // Australia & Pacific
    'Australia/Melbourne',
    'Australia/Sydney',
    'Pacific/Auckland',

    // Africa
    'Africa/Cairo',
    'Africa/Johannesburg',
    'Africa/Lagos',
  ];
}

/**
 * Step configuration: order, labels, and skip behavior.
 * Icons are rendered separately in the component layer (to avoid JSX in constants).
 */
export const ONBOARDING_STEPS: OnboardingStepConfig[] = [
  {
    key: 'concierge',
    label: 'Quick Setup',
    iconName: 'Sparkles',
    canSkip: true,
    requiresBusinessId: false,
    showInProgress: false,
  },
  {
    key: 'business-name',
    label: 'Business Name',
    iconName: 'Building2',
    canSkip: false,
    requiresBusinessId: false,
    showInProgress: true,
  },
  {
    key: 'business-type',
    label: 'Type',
    iconName: 'Grid3x3',
    canSkip: true,
    requiresBusinessId: true,
    showInProgress: true,
  },
  {
    key: 'import-clients',
    label: 'Your Clients',
    iconName: 'Upload',
    canSkip: true,
    requiresBusinessId: true,
    showInProgress: true,
  },
  {
    key: 'services',
    label: 'Services',
    iconName: 'Scissors',
    canSkip: false,
    requiresBusinessId: true,
    showInProgress: true,
  },
  {
    key: 'timezone',
    label: 'Timezone',
    iconName: 'Globe',
    canSkip: false,
    requiresBusinessId: true,
    showInProgress: true,
  },
  {
    key: 'location',
    label: 'Location (Optional)',
    iconName: 'MapPin',
    canSkip: true,
    requiresBusinessId: true,
    showInProgress: false, // Don't show optional step in progress indicator
  },
  {
    key: 'hours',
    label: 'Hours',
    iconName: 'Clock',
    canSkip: false,
    requiresBusinessId: true,
    showInProgress: true,
  },
  {
    key: 'payments',
    label: 'Payments',
    iconName: 'CreditCard',
    canSkip: true,
    requiresBusinessId: true,
    showInProgress: true,
  },
  {
    key: 'reminders',
    label: 'Reminders',
    iconName: 'Bell',
    canSkip: false,
    requiresBusinessId: true,
    showInProgress: true,
  },
  {
    key: 'complete',
    label: 'Complete',
    iconName: 'Check',
    canSkip: false,
    requiresBusinessId: true,
    showInProgress: false,
  },
];

/**
 * Create default/empty draft state.
 */
export function createEmptyDraft() {
  const defaultTimezone = getDetectedTimezone();

  return {
    businessName: '',
    businessSlug: '',
    businessType: undefined,
    selectedTemplateId: undefined,
    services: [],
    serviceSource: undefined,
    detectedTimezone: defaultTimezone,
    selectedTimezone: defaultTimezone,
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    businessHours: DAYS_OF_WEEK.reduce(
      (acc, day) => {
        acc[day] = {
          open: '09:00',
          close: '17:00',
          closed: day === 'sunday',
        };
        return acc;
      },
      {} as Record<string, { open: string; close: string; closed: boolean }>
    ),
    reminderEnabled: true,
    reminderHours: 24,
    conciergeSource: undefined,
  };
}
